import './Map.css'
import 'leaflet/dist/leaflet.css'

import React, { useEffect, useState } from 'react'
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet'
import L, { LatLngExpression } from 'leaflet'
import { CurrentSession } from '@/services/repository/session'
import { Services } from '@/services/services'
import { useNavigate } from 'react-router-dom'
import { useModal } from '../modal/Modal'
import { SuperAdminInsertMachinesPopup } from '@/pages/for/superadmin/machines/Machines'
import { Roles } from '@/services/repository/users'
import { defaultMapCenter } from '@/env/config'
import { formatDate } from '@/common/time'

import { brandColors } from './const'
import { useAppContext } from '@/context/app'
import { MachineCreateWizard } from '@/popups/machines/wizard/create/Main'
import { MachineName } from '../machine'

export const vehicleIconSize = 16

export const activeTime = 5 * 60 * 1000; // fiveMinutesInMs

interface MapRowMachine {
  id: string | undefined
  name: string | undefined
  internalNumber: string | undefined
  type: {
    manufacturer: string;
    category: string;
    model: string;
  }
}

interface MapRowstatus {
  fire: boolean | null
}

export interface MapRow {
  imei: string
  time: string // ISO 8601 string representation of DtServer (e.g., "2025-01-17T12:34:56Z")
  dtTracker: string // ISO 8601 string representation of DtTracker (e.g., "2025-01-17T12:34:56Z")
  lat: number  // Latitude as a floating-point number
  lng: number  // Longitude as a floating-point number
  altitude: number // Altitude in meters
  angle: number // Angle in degrees
  speed: number // Speed in meters per second
  soc: number // SOC
  engineHours: number // MTH of engine
  ucid: string // User Card ID
  machine: MapRowMachine
  status: MapRowstatus
}

export interface MapResponse {
  time: string // ISO 8601 string representation of the latest DtServer
  rows: MapRow[] | null   // Array of MapRow objects
}

export function mergeMapResponses(oldResponse: MapResponse, newResponse: MapResponse): MapResponse {
  if (!newResponse.rows) {
    return oldResponse
  }
  const updatedRows: MapRow[] = oldResponse.rows ? [...oldResponse.rows] : []
  const imeiMap = new Map<string, MapRow>(updatedRows.map(row => [row.imei, row]))
  for (const newRow of newResponse.rows) {
    imeiMap.set(newRow.imei, newRow)
  }
  const mergedRows = Array.from(imeiMap.values());
  return {
    time: newResponse.time,
    rows: mergedRows,
  }
}

export const MapPopupBody = ({ services, session, row }: { services: Services, session: CurrentSession, row: MapRow }) => {
  const navigate = useNavigate()
  const { openModal } = useModal()
  return <div className='map-popup-wrap' onClick={() => row.machine.id && navigate(`/communal/machine/${row.machine.id}`)}>
    <MachineName row={row.machine} />
    {!row.machine.id ? <p style={{ cursor: 'pointer' }} onClick={() => session.user.role === Roles.SuperAdmin && openModal((props) => MachineCreateWizard({
      ...props, services, session, machine: {
        imei: row.imei,
      }, onDone: (id: string) => {
        navigate(`/communal/machine/${id}`)
      }
    }))}>{`IMEI: ${row.imei}`}</p> : null}
    {row.machine.id ? <p style={{ cursor: 'pointer' }}>{row.machine.name}</p> : null}
    <p>{session.translates.for('map.speed')}: {row.speed} km/h</p>
    <p>{session.translates.for('map.direction')}: {row.angle}°</p>
    {row.soc ? <p>{session.translates.for('map.soc')}: {row.soc}%</p> : null}
    {row.engineHours ? <p>{session.translates.for('map.engineHours')}: {row.engineHours}MTH</p> : null}
    <p>Fire: {row.status.fire === null ? '-' : row.status.fire ? 'Tak' : 'Nie'}</p>
    {row.ucid ? <p>{session.translates.for('map.ucid')}:<br /> {row.ucid}</p> : null}
    <p className='map-point-dttracker'>{formatDate(new Date(row.dtTracker))}</p>
    {session.user.role === Roles.SuperAdmin && !row.machine.id ? <p className='main-btn' onClick={() => openModal((props) => MachineCreateWizard({
      ...props, services, session, machine: {
        imei: row.imei,
      }, onDone: (id: string) => {
        navigate(`/communal/machine/${id}`)
      }
    }))}>{session.translates.for('map.add_btn')}</p> : null}

    {row.machine.id ? <p className='main-btn' onClick={() => navigate(`/communal/machine/${row.machine.id}`)}>{session.translates.for('map.machine_page')}</p> : null}
  </div>
}

const styleString = (style: any) => (
  Object.entries(style).map(([k, v]) => `${k}:${v}`).join(';')
)

function isActive(dateStr: string): boolean {
  const now = Date.now()
  const inputDate = new Date(dateStr).getTime();
  if (isNaN(inputDate)) {
    throw new Error("Invalid date string");
  }
  return now - inputDate <= activeTime && inputDate <= now;
}

export function vehicleCSS(row: MapRow): string {
  let css: any = {
    width: vehicleIconSize + 'px',
    height: vehicleIconSize + 'px',
    transform: `rotate(${row.angle + 45}deg)`,
    'transform-origin': 'center'
    // opacity: isActive(row.dtTracker) ? '1' : '0.33',
  }
  // if (row.status.fire = true) {
  //   css.filter = `drop-shadow(100vw 0 0 ${brandColors.active})`
  //   css.transform = 'translateX(-100vw)'
  // }
  return styleString(css)
}

export function vehicleClass(row: MapRow): string {
  if(!row.machine.id) {
    return 'map-point-vehicle-new'
  } else if (!isActive(row.dtTracker)) {
    return 'map-point-vehicle-inactive'
  } else if (row.status.fire) {
    return 'map-point-vehicle-fire'
  } 
  return 'map-point-vehicle-active'
}

export function vehicleElement(row: MapRow): string {
  return `<div class="${vehicleClass(row)}">`
    + (row.angle ?
      `<img src="/public/img/gps/vehicle_arrow.png" style="${vehicleCSS(row)}" />`
      : `<img src="/public/img/gps/map_point.png" style="${vehicleCSS(row)}" />`) +
    `</div>`
}

export function vehicleObject(row: MapRow): object {
  var halfVehicleIconSize = vehicleIconSize/2
  return {
    className: 'car-icon',
    html: vehicleElement(row),
    iconSize: [vehicleIconSize, vehicleIconSize], // Adjust size as needed
    iconAnchor: [halfVehicleIconSize, halfVehicleIconSize], // Anchor point
  }
}

export const MapComponent = ({ fetchUrl }: {
  fetchUrl: string
}) => {
  const { services, session, translates } = useAppContext()
  const [map, setMap] = useState<MapResponse | undefined>(undefined)
  const [myposition, setMyPosition] = useState<[number, number] | undefined>(undefined)
  const navigate = useNavigate()
  const { openModal } = useModal()

  async function refreshMap(fromDate?: Date, oldResponse?: MapResponse): Promise<MapResponse> {
    return services.session.fetchWithToken(`${fetchUrl}?fromDate=${fromDate ? fromDate.toISOString() : ''}`).then(async (r) => {
      if (r.status !== 200) {
        const erros = await r.json()
        console.error(erros)
        return Promise.reject<MapResponse>()
      }
      const newResponse: MapResponse = await r.json()
      newResponse.rows = newResponse.rows || null
      if (oldResponse === undefined) {
        return newResponse
      }
      return mergeMapResponses(oldResponse, newResponse)
    })
  }

  useEffect(() => {
    // Function to handle geolocation success
    const handleSuccess = (location: GeolocationPosition) => {
      const { latitude, longitude } = location.coords
      setMyPosition([latitude, longitude])
    }

    // Function to handle geolocation error
    const handleError = (error: GeolocationPositionError) => {
      console.error('Error obtaining location:', error)
      // Fallback to a default position if geolocation fails
      setMyPosition(undefined)
    }

    // Check if geolocation is available
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(handleSuccess, handleError)
    } else {
      console.error('Geolocation is not supported by this browser.')
      // Fallback to a default position if geolocation is not supported
      setMyPosition(undefined)
    }
  }, [])

  useEffect(() => {
    refreshMap().then(setMap)
  }, []);

  useEffect(() => {
    if (map === undefined) {
      return
    }
    const clock = async () => {
      if (map === undefined) {
        return
      }
      await refreshMap(map?.time ? new Date(map?.time) : undefined, map).then(setMap)
      handle = setTimeout(clock, 2000)
    }
    let handle: NodeJS.Timeout = setTimeout(clock, 2000)
    return () => {
      clearTimeout(handle)
    }
  }, [map])

  if (map === undefined) {
    return <div className='spinner'></div>
  }

  function findLatestRow(response: MapResponse | undefined): MapRow | undefined {
    if (!response?.rows || response.rows.length === 0) {
      return undefined;
    }

    return response.rows.reduce((latest, current) => {
      return new Date(current.time) > new Date(latest.time) ? current : latest;
    });
  }

  const firstRow = findLatestRow(map);

  const center: LatLngExpression = (firstRow && {
    lat: firstRow.lat,
    lng: firstRow.lng
  }) || myposition || defaultMapCenter

  return (
    <MapContainer attributionControl={false} center={center} zoom={13} style={{ height: '100%', width: '100%' }}>
      <TileLayer
        attribution='evoltag.pl'
        url='https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'
      />
      {map.rows?.map((row) => (
        <Marker
          key={row.imei}
          position={row}
          icon={L.divIcon(vehicleObject(row))} >
          <Popup>
            <MapPopupBody row={row} services={services} session={session} />
            {/* <div className='map-popup-wrap' onClick={() => row.machine.id && navigate(`/communal/machine/${row.machine.id}`)}>
              {!row.machine.id ? <p style={{ cursor: 'pointer' }} onClick={() => session.user.role === Roles.SuperAdmin && openModal((props) => SuperAdminInsertMachinesPopup({
                ...props, services, session, defaultValues: {
                  imei: row.imei,
                }, onInsert: ({ id }) => {
                  navigate(`/communal/machine/${id}`)
                }
              }))}>{`IMEI: ${row.imei}`}</p> : null}
              {row.machine.id ? <p style={{ cursor: 'pointer' }}>{row.machine.name}</p> : null}
              <p>{session.translates.for('map.speed')}: {row.speed} km/h</p>
              <p>{session.translates.for('map.direction')}: {row.angle}°</p>
              {row.soc ? <p>{session.translates.for('map.soc')}: {row.soc}%</p> : null}
              {row.engineHours ? <p>{session.translates.for('map.engineHours')}: {row.engineHours}MTH</p> : null}
              <p>Fire: {row.status.fire === null ? '-' : row.status.fire ? 'Tak' : 'Nie'}</p>
              {row.ucid ? <p>{session.translates.for('map.ucid')}:<br /> {row.ucid}</p> : null}
              <p className='map-point-dttracker'>{formatDate(new Date(row.dtTracker))}</p>
              {session.user.role === Roles.SuperAdmin && !row.machine.id ? <p className='main-btn' onClick={() => openModal((props) => SuperAdminInsertMachinesPopup({
                ...props, services, session, defaultValues: {
                  imei: row.imei,
                }, onInsert: ({ id }) => {
                  navigate(`/communal/machine/${id}`)
                }
              }))}>{session.translates.for('map.add_btn')}</p> : null}

              {row.machine.id ? <p className='main-btn' onClick={() => navigate(`/communal/machine/${row.machine.id}`)}>{session.translates.for('map.machine_page')}</p> : null}
            </div> */}
          </Popup>
        </Marker>
      ))}

      {myposition ? <Marker
        key='my-location'
        position={myposition}
        icon={L.divIcon({
          className: 'car-icon',
          html: `<img src="/public/img/gps/i_am_here.png" style="width: 32px;" />`,
          iconSize: [32, 32], // Adjust size as needed
          iconAnchor: [16, 16], // Anchor point
        })}></Marker> : null}
    </MapContainer>
  )
}

export default MapComponent
